/**
 * QuickRepliesPanel Component
 * Drafts a response using the contact's linked agent.
 * Uses the agent from the most recent assistant message.
 */

import { useState } from "react"
import { Button } from "@/components/ui/button"
import { Card } from "@/components/ui/card"
import { Loader2, Sparkles, X } from "lucide-react"
import { agents } from "@/lib/agents"
import { app } from "@/lib/app"
import { useTeamStore } from "@/lib/hooks/useTeam"
import { ModerationContact, ModerationMessage } from "../types"
import { isIncomingMessage } from "../utils/conversationUtils"

interface QuickRepliesPanelProps {
  onSelect: (text: string) => void
  onClose: () => void
  contact: ModerationContact
  messages?: ModerationMessage[]
}

export function QuickRepliesPanel({ onSelect, onClose, contact, messages }: QuickRepliesPanelProps) {
  const [drafting, setDrafting] = useState(false)
  const [draftedResponse, setDraftedResponse] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const { selectedTeam } = useTeamStore()

  // Find the agent from the most recent assistant message
  const agentId = [...(messages || [])].reverse().find(m => m.role === "assistant" && m.agent)?.agent

  const handleDraftResponse = async () => {
    if (!agentId || !selectedTeam?.id) {
      setError("No agent configured for this contact")
      return
    }

    setDrafting(true)
    setError(null)
    setDraftedResponse(null)

    try {
      const apiKeys = await app.fetchAPIkeys(selectedTeam.id)
      const agentInstance = await agents.loadAgent(agentId as any, selectedTeam.id)
      if (!agentInstance) throw new Error("Agent not found")

      const lastIncoming = (messages || []).filter(m => isIncomingMessage(m)).pop()
      const messageText = lastIncoming?.message || ""

      const p: AgentParameters = {
        debug: false,
        uid: contact.id,
        input: { message: messageText },
        apiKeys,
      }

      await agentInstance.execute(p)
      const response = p.output?.response || ""
      if (!response) throw new Error("Agent returned no response")

      setDraftedResponse(response)
    } catch (err) {
      console.error("[QuickRepliesPanel] draft failed:", err)
      setError(err instanceof Error ? err.message : "Failed to draft response")
    } finally {
      setDrafting(false)
    }
  }

  return (
    <Card className="p-3 mb-2">
      <div className="flex items-center justify-between mb-2">
        <h3 className="text-xs font-semibold flex items-center gap-1.5">
          <Sparkles className="h-3.5 w-3.5 text-purple-500" />
          Draft Response with Agent
        </h3>
        <Button variant="ghost" size="icon" className="h-6 w-6" onClick={onClose}>
          <X className="h-3 w-3" />
        </Button>
      </div>

      {!agentId ? (
        <p className="text-xs text-muted-foreground">
          No agent is linked to this conversation.
        </p>
      ) : draftedResponse ? (
        <div className="space-y-2">
          <div className="text-xs bg-muted rounded p-2 max-h-[120px] overflow-y-auto whitespace-pre-wrap">
            {draftedResponse}
          </div>
          <div className="flex gap-2">
            <Button size="sm" className="flex-1 h-7 text-xs" onClick={() => { onSelect(draftedResponse); setDraftedResponse(null) }}>
              Use This Response
            </Button>
            <Button size="sm" variant="outline" className="h-7 text-xs" onClick={handleDraftResponse}>
              Regenerate
            </Button>
          </div>
        </div>
      ) : (
        <div className="space-y-2">
          {error && <p className="text-xs text-destructive">{error}</p>}
          <Button size="sm" onClick={handleDraftResponse} disabled={drafting} className="w-full h-8 text-xs">
            {drafting ? <Loader2 className="h-3 w-3 mr-1.5 animate-spin" /> : <Sparkles className="h-3 w-3 mr-1.5" />}
            {drafting ? "Drafting..." : "Draft a Response"}
          </Button>
        </div>
      )}
    </Card>
  )
}
